import { message, Spin } from "antd";
import Title from "antd/lib/typography/Title";
import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { BUTTON_SIZE, INPUT_SIZE } from "../../../common/ThemeConstants";
import FormButton from "../../../components/general/FormButton";
import FormContainer from "../../../components/general/FormContainer";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import ReportExcelDownload from "../../../components/ReportingComponents/ReportExcelDownload";
import { postRequest } from "../../../services/mainApp.service";
import { getDate } from "../../../functions/dateFunctions";
import StockLedgerReportTemp from "./StockLedgerReportTemp";
import { SearchOutlined } from "@ant-design/icons";

const initialSearchValues = {
  OperationId: 1,
  CompanyId: null,
  BranchId: null,
  DepartmentId: null,
  CategoryId: null,
  SubCategoryId: null,
  ItemId: null,
  DataType: null,
  DateFrom: "",
  DateTo: "",
};

const StockReport = () => {
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const [data, setData] = useState(initialSearchValues);
  const [list, setList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [disableExcel, setDisableExcel] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    postRequest(
      "/Inv_Stock_LedgerReport",
      {
        ...data,
        CompanyId: userData.CompanyId,
        OperationId: 1,
        DataType: null,
        DateFrom: data.DateFrom || getDate(),
        DateTo: data.DateTo || getDate(),
      },
      controller
    )
      .then((res) => {
        if (res.error) {
          message.error(res.errorMessage || "Failed to fetch dropdown data");
          return;
        }
        const additionalData = res?.data?.AdditionalData || {};
        const dataSet = res?.data?.DataSet || {};
        setBranchList(additionalData.Branches || dataSet.Table || []);
        setDepartmentList(additionalData.Departments || dataSet.Table1 || []);
        setCategoryList(additionalData.Categories || dataSet.Table2 || []);
        setSubCategoryList(additionalData.SubCategories || dataSet.Table3 || []);
        setItemList(additionalData.Items || dataSet.Table4 || []);
        setData((prev) => ({
          ...prev,
          DateFrom: prev.DateFrom || getDate(),
          DateTo: prev.DateTo || getDate(),
        }));
      })
      .catch((err) => {
        if (err.message !== "canceled") {
          console.error(err);
          message.error("Failed to fetch dropdown data");
        }
      });
    return () => {
      controller.abort();
    };
  }, []);

  const handleSearchChange = (data) => {
    setData((prev) => ({ ...prev, [data.name]: data.value }));
    setList([]);
    setDisableExcel(true);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!data.BranchId) {
      message.error("Please select Branch");
      return;
    }
    if (!data.DepartmentId) {
      message.error("Please select Department");
      return;
    }
    setLoading(true);
    if (data.DateFrom && data.DateTo) {
      if (new Date(data.DateFrom) > new Date(data.DateTo)) {
        message.error("Date From must be less than or equal to Date To");
        setLoading(false);
        return;
      }
    }
    postRequest("/Inv_Stock_LedgerReport", {
      ...data,
      CompanyId: userData.CompanyId,
      OperationId: 1,
      DataType: "StockReport",
      DateFrom: data.DateFrom || getDate(),
      DateTo: data.DateTo || getDate(),
    })
      .then((res) => {
        setLoading(false);
        if (res.error) {
          message.error(res.errorMessage || "Failed to fetch report");
          setList([]);
          setDisableExcel(true);
          return;
        }
        const reportData = res?.data?.Data || res?.data?.DataSet?.Table || [];
        if (!reportData.length) {
          message.error("No records found!");
          setList([]);
          setDisableExcel(true);
          return;
        }
        setList(reportData);
        setDisableExcel(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err.message !== "canceled") {
          console.error(err);
          message.error("Failed to fetch report");
        }
        setList([]);
        setDisableExcel(true);
      });
  };

  const searchPanel = (
    <Fragment>
      <FormSelect
        colSpan={6}
        listItem={branchList || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="BranchId"
        label="Branch"
        value={data.BranchId}
        onChange={handleSearchChange}
        allowClear={true}
        required={true}
      />

      <FormSelect
        colSpan={6}
        listItem={departmentList || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="DepartmentId"
        label="Department"
        value={data.DepartmentId}
        onChange={handleSearchChange}
        allowClear={true}
        required={true}
      />

      <FormSelect
        colSpan={6}
        listItem={categoryList || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="CategoryId"
        label="Category"
        value={data.CategoryId}
        onChange={handleSearchChange}
        allowClear={true}
      />

      <FormSelect
        colSpan={6}
        listItem={
          subCategoryList.filter(
            (x) => x.CategoryId === data.CategoryId || !data.CategoryId
          ) || []
        }
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="SubCategoryId"
        label="Sub Category"
        value={data.SubCategoryId}
        onChange={handleSearchChange}
        allowClear={true}
      />

      <FormSelect
        colSpan={6}
        listItem={
          itemList.filter(
            (x) =>
              (x.SubCategoryId === data.SubCategoryId || !data.SubCategoryId) &&
              (x.CategoryId === data.CategoryId || !data.CategoryId)
          ) || []
        }
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="ItemId"
        label="Item"
        value={data.ItemId}
        onChange={handleSearchChange}
        allowClear={true}
      />

      {/* <FormTextField
        colSpan={6}
        label="Date From"
        type="date"
        name="DateFrom"
        size={INPUT_SIZE}
        value={data.DateFrom}
        onChange={handleSearchChange}
      /> */}

      <FormTextField
        colSpan={6}
        label="Date"
        type="date"
        name="DateTo"
        size={INPUT_SIZE}
        value={data.DateTo}
        onChange={handleSearchChange}
      />

      <FormButton
        title="Search"
        type="primary"
        size={BUTTON_SIZE}
        colSpan={2}
        htmlType="submit"
        icon={<SearchOutlined />}
      />
    </Fragment>
  );

  return (
    <div style={{ margin: 5 }}>
      <Title level={3} style={{ color: "#017971" }}>
        Stock Report
      </Title>
      <div
        style={{
          background: "white",
          padding: 15,
          boxShadow: "0 0 5px lightgray",
          borderRadius: 2,
        }}
      >
        <FormContainer
          rowStyle={{ alignItems: "flex-end" }}
          onSubmit={handleSearchSubmit}
        >
          {searchPanel}
        </FormContainer>
        <div
          style={{ margin: "10px 0", borderTop: "1px solid lightgray" }}
        ></div>
      </div>

      <ReportExcelDownload fileName={`Stock_Report_${data.DateFrom}_${data.DateTo}`}>
        {loading && (
          <Spin
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          />
        )}
        {list.length > 0 && (
          <StockLedgerReportTemp
            list={list}
            headList={list.length > 0 ? Object.keys(list[0]) : []}
            date={`Till Date: ${data.DateTo}`}
            branch={
              branchList?.find((x) => x.Id === data.BranchId || x.BranchId === data.BranchId)?.Name ||
              branchList?.find((x) => x.Id === data.BranchId || x.BranchId === data.BranchId)?.BranchName ||
              ""
            }
            department={
              departmentList?.find((x) => x.Id === data.DepartmentId)?.Name ||
              departmentList?.find((x) => x.Id === data.DepartmentId)?.DepartmentName ||
              ""
            }
          />
        )}
      </ReportExcelDownload>
    </div>
  );
};

export default StockReport;
